import { useState, type FormEvent, type JSX, type ReactNode } from 'react';
import {
  formatExactMoney,
  type BusinessAction,
} from '@cryptology-assist/domain';
import type { Copy, Language } from './copy';
import { dateLabel } from './copy';
import { ApiError } from './api';

function configurationFieldLabel(path: string, copy: Copy): string {
  const labels: Record<string, string> = {
    'adviser.name': copy.adviserName,
    'adviser.email': copy.adviserEmail,
    'adviser.timezone': copy.timezone,
    'adviser.weeklyHours': copy.adviserWorkingHours,
    'adviser.exceptions': copy.availabilityExceptions,
    'adviser.botIds': copy.authorizedAssistants,
    adviser: copy.editAdviser,
    'business.description': copy.businessDescription,
    'business.audience': copy.businessAudience,
    'profile.assistantName': copy.name,
    'profile.businessName': copy.businessName,
    'profile.personality': copy.personality,
    'profile.instructions': copy.instructions,
    'responseModel.maxOutputTokens': copy.responseOutputTokens,
    'responseModel.processingMode': copy.responseProcessingMode,
    'responseModel.reasoningEffort': copy.reasoningEffort,
    profile: copy.personalityAndSales,
    business: copy.businessIdentity,
    responseModel: copy.modelSettings,
    salesPlaybook: copy.personalityAndSales,
    capabilities: copy.capabilities,
    commercialOutcomes: copy.capabilities,
    leadDestination: copy.leadDestination,
    appointmentServices: copy.appointmentServices,
    calendar: copy.calendar,
    channels: copy.channels,
    businessConnectionId: copy.businessConnection,
  };
  return (
    labels[path] ??
    labels[path.split('.').slice(0, 2).join('.')] ??
    labels[path.split('.')[0]!] ??
    copy.configuration
  );
}

export function ErrorNotice({
  error,
  copy,
  onRetry,
}: {
  error: Error | null;
  copy: Copy;
  onRetry?: () => void;
}): JSX.Element | null {
  if (!error) return null;
  return (
    <div className="notice error" role="alert">
      <strong>{copy.error}</strong>
      <span>
        {error.message === 'SERVICE_RESPONSE_INVALID'
          ? copy.serviceUnavailable
          : error.message in copy
            ? copy[error.message as keyof Copy]
            : error.message}
      </span>
      {error instanceof ApiError && error.fields.length > 0 && (
        <ul>
          {[
            ...new Set(
              error.fields.map((path) => configurationFieldLabel(path, copy))
            ),
          ].map((label) => (
            <li key={label}>{label}</li>
          ))}
        </ul>
      )}
      {onRetry && <button onClick={onRetry}>{copy.retry}</button>}
    </div>
  );
}
export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}
export function useMutation() {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<Error | null>(null);
  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (error) {
      setError(error instanceof Error ? error : new Error('REQUEST_FAILED'));
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, run };
}
export function ActionSummary({
  action,
  copy,
  language,
}: {
  action: BusinessAction;
  copy: Copy;
  language: Language;
}): JSX.Element {
  if (action.kind === 'create_lead')
    return (
      <div className="action-summary">
        <strong>{copy.lead}</strong>
        <p>
          {action.firstName} {action.lastName} · {action.email} · {action.phone}
        </p>
        <p>{action.request}</p>
      </div>
    );
  if (
    action.kind === 'book_appointment' ||
    action.kind === 'request_sales_appointment'
  )
    return (
      <div className="action-summary">
        <strong>
          {copy.appointment}
          {action.kind === 'book_appointment' && ` · ${action.subject}`}
        </strong>
        <p>
          {action.kind === 'book_appointment'
            ? action.name
            : `${action.firstName} ${action.lastName}`}{' '}
          · {action.email}
        </p>
        {action.kind === 'request_sales_appointment' && (
          <>
            <p>{action.phone}</p>
            <p>{action.request}</p>
          </>
        )}
        <p>
          {dateLabel(action.startsAt, language)} —{' '}
          {dateLabel(action.endsAt, language)}
        </p>
      </div>
    );
  if (
    action.kind === 'cancel_appointment' ||
    action.kind === 'reschedule_appointment'
  )
    return (
      <div className="action-summary">
        <strong>
          {action.kind === 'cancel_appointment'
            ? copy.cancelAppointment
            : copy.rescheduleAppointment}
        </strong>
        <p>
          {copy.reference}: {action.appointmentActionId}
        </p>
        {action.kind === 'cancel_appointment' ? (
          <p>{action.reason}</p>
        ) : (
          <p>
            {dateLabel(action.startsAt, language)} —{' '}
            {dateLabel(action.endsAt, language)}
          </p>
        )}
      </div>
    );
  if (action.kind === 'list_appointments')
    return <div className="action-summary">{copy.appointments}</div>;
  if (action.kind === 'find_appointment_slots')
    return <div className="action-summary">{copy.calendar}</div>;
  if (action.kind === 'payment_proposal')
    return (
      <div className="action-summary">
        <strong>{copy.paymentProposal}</strong>
        <p>
          {copy.reference}: {action.reference}
        </p>
        <p>
          {formatExactMoney(
            BigInt(action.amountMinor),
            action.currency,
            2,
            language === 'es' ? 'es-PE' : 'en-US'
          )}{' '}
          · {action.date}
        </p>
        <p>{action.explanation}</p>
      </div>
    );
  if (action.kind === 'payment_claim' || action.kind === 'dispute')
    return (
      <div className="action-summary">
        <strong>
          {action.kind === 'payment_claim' ? copy.payment_claim : copy.dispute}
        </strong>
        <p>{action.reference}</p>
        <p>{action.explanation}</p>
      </div>
    );
  if (action.kind === 'handoff')
    return (
      <div className="action-summary">
        <strong>{copy.human}</strong>
        <p>{action.reason}</p>
      </div>
    );
  return (
    <div className="action-summary">
      <strong>{copy.request}</strong>
      <p>
        {action.kind === 'search_offerings'
          ? action.query
          : action.kind === 'calendar_availability'
            ? `${dateLabel(action.startsAt, language)} — ${dateLabel(action.endsAt, language)}`
            : copy.collections}
      </p>
    </div>
  );
}
export function onSubmit(event: FormEvent, work: () => Promise<void>): void {
  event.preventDefault();
  void work();
}
